<template>
  <div>
    <!-- Page Header -->
    <CCard class="mb-4 border-0">
      <CCardBody class="p-4">
        <CRow class="align-items-center">
          <CCol md="7">
            <h1 class="h4 mb-1 font-weight-bold">
              {{ obj.id ? `Purchase order ${obj.orderNumber || ""}` : "Create purchase order" }}
              <CBadge
                v-if="obj.id"
                :color="badgeColor('purchaseOrder', obj.statusDescription || 'Draft')"
                class="ml-2 align-middle"
              >
                {{ badgeLabel("purchaseOrder", obj.statusDescription || "Draft") }}
              </CBadge>
              <CBadge
                v-if="hasBills && billingBadgeVisible"
                :color="badgeColor('purchaseOrderBilling', obj.billingStatus)"
                class="ml-1 align-middle"
              >
                {{ badgeLabel("purchaseOrderBilling", obj.billingStatus) }}
              </CBadge>
            </h1>
            <p class="text-muted mb-0">
              {{
                obj.id
                  ? selectedVendor && selectedVendor.name
                    ? `For ${selectedVendor.name}`
                    : "Edit this purchase order"
                  : "Create a new purchase order for your vendor"
              }}
            </p>
          </CCol>
          <CCol md="5" class="d-flex justify-content-md-end align-items-start mt-3 mt-md-0">
            <div v-if="showCreateBill" class="text-md-right mr-2">
              <CButton color="primary" :disabled="!canCreateBill || creatingBill" @click="createBill">
                <CIcon name="cil-plus" class="mr-1" />
                {{ creatingBill ? copy.creatingBill : copy.createBill }}
              </CButton>
              <div v-if="createBillBlockedReason" class="small text-muted mt-1">{{ createBillBlockedReason }}</div>
            </div>
            <CDropdown
              v-if="obj.id"
              placement="bottom-end"
              toggler-text="Actions"
              color="light"
            >
              <CDropdownItem @click="addNew()">New purchase order</CDropdownItem>
              <template v-if="statusTransitions.length">
                <CDropdownDivider />
                <CDropdownHeader>Change status</CDropdownHeader>
                <CDropdownItem
                  v-for="status in statusTransitions"
                  :key="status.id"
                  @click="askChangeState(status)"
                >
                  Mark as {{ badgeLabel("purchaseOrder", status.name).toLowerCase() }}
                </CDropdownItem>
              </template>
            </CDropdown>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>

    <!-- Bills raised from this order -->
    <CCard v-if="hasBills" class="mb-4 border-0">
      <CCardHeader class="bg-transparent border-0 p-4">
        <h2 class="h6 font-weight-bold mb-0">
          {{ copy.billsTitle }}
          <CBadge :color="badgeColor('purchaseOrderBilling', obj.billingStatus)" class="ml-2">
            {{ badgeLabel("purchaseOrderBilling", obj.billingStatus) }}
          </CBadge>
        </h2>
        <div class="small text-muted">{{ copy.lockedNote }}</div>
      </CCardHeader>
      <CCardBody class="pt-0 pb-4 px-4">
        <ul class="list-unstyled mb-3">
          <li v-for="bill in obj.bills" :key="bill.id" class="mb-1">
            <router-link :to="`/tenants/Bill/${bill.id}`" class="font-weight-bold text-monospace">{{ bill.billNo }}</router-link>
            <CBadge :color="badgeColor('bill', bill.state)" class="ml-2">{{ badgeLabel("bill", bill.state) }}</CBadge>
          </li>
        </ul>
        <div class="table-responsive">
          <table class="table table-sm mb-0">
            <thead>
              <tr>
                <th>Item</th>
                <th class="text-right">Ordered</th>
                <th class="text-right">Billed</th>
                <th class="text-right">Remaining</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="line in billLines" :key="line.key">
                <td>{{ line.name }}</td>
                <td class="text-right">{{ line.ordered }}</td>
                <td class="text-right">{{ line.billed }}</td>
                <td class="text-right">{{ line.remaining }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </CCardBody>
    </CCard>

    <!-- Main Form -->
    <CRow>
      <CCol lg="12">
        <CCard class="mb-4 border-0">
          <CCardHeader class="bg-transparent border-0 p-4">
            <h2 class="h6 font-weight-bold mb-0">Purchase order details</h2>
          </CCardHeader>
          <CCardBody class="p-4">
            <fieldset :disabled="isLocked" class="border-0 p-0 m-0 min-w-0">
            <!-- Vendor & Purchase Order Info -->
            <CRow>
              <CCol md="6">
                <div class="form-group" ref="vendorField">
                  <label for="po-vendor">Vendor *</label>
                  <v-select
                    inputId="po-vendor"
                    style="width: 100%"
                    v-model="selectedVendor"
                    :label="'name'"
                    :options="vendorItemsWithAddNew"
                    placeholder="Select vendor"
                    :disabled="isLocked"
                    :class="{ 'is-invalid': submitted && !hasVendor }"
                    @input="handleVendorSelect"
                  >
                    <span slot="no-options" @click="addNewVendor">
                      Add new vendor
                    </span>
                  </v-select>
                  <div v-if="submitted && !hasVendor" class="invalid-feedback d-block">
                    Choose a vendor, or add a new one
                  </div>
                </div>

                <div
                  v-if="hasVendor"
                  class="mb-3 p-3 border rounded bg-light"
                >
                  <div class="small text-muted mb-1">Vendor details</div>
                  <div class="font-weight-medium">{{ selectedVendor.name }}</div>
                  <div class="small" v-if="selectedVendor.contactEmail">
                    <a :href="`mailto:${selectedVendor.contactEmail}`">{{ selectedVendor.contactEmail }}</a>
                  </div>
                  <div class="small" v-if="selectedVendor.contactPhone">
                    <a :href="`tel:${selectedVendor.contactPhone}`">{{ selectedVendor.contactPhone }}</a>
                  </div>
                </div>
              </CCol>

              <CCol md="6">
                <CInput
                  label="PO number"
                  description="Created automatically if you leave it empty"
                  v-model="obj.orderNumber"
                />
              </CCol>
            </CRow>

            <CRow>
              <CCol md="4">
                <CInput
                  label="Issue date"
                  type="date"
                  :value="computeIssuedDate"
                  @change="setIssuedDate"
                />
              </CCol>
              <CCol md="4">
                <CInput
                  label="Expected date"
                  type="date"
                  :value="computeExpiryDate"
                  @change="setExpiryDate"
                  :is-valid="expectedDateValid ? null : false"
                  invalid-feedback="Choose a date on or after the issue date"
                />
              </CCol>
              <CCol md="4">
                <CInput
                  label="Reference (optional)"
                  v-model="obj.reference"
                  placeholder="e.g. vendor quotation number"
                />
              </CCol>
            </CRow>

            <CRow>
              <CCol>
                <CTextarea
                  label="Title or description (optional)"
                  v-model="obj.title"
                  placeholder="What this purchase order is for"
                  rows="3"
                />
              </CCol>
            </CRow>
            </fieldset>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <!-- Items Section -->
    <CRow>
      <CCol lg="12">
        <CCard class="mb-4 border-0">
          <CCardBody class="p-4" ref="itemsField">
            <InvoiceItemsComponent
              :items="normalizedPurchaseOrderItems"
              :productItems="computeProductItemsWithAddNew"
              :grandTotal="grandTotal"
              :grandTaxOnly="0"
              :grandTotalWithTax="grandTotal"
              :selectedSalesTax="null"
              :salesTaxItems="[]"
              itemType="purchase-order"
              :showTax="false"
              :useDescriptionModal="true"
              :readonly="isLocked"
              @add-item="addNewItem"
              @remove-item="onRemoveClaimItem"
              @move-item="moveItem"
              @product-selected="handleProductSelectNormalized"
            />
            <div v-if="submitted && !itemsValid" class="invalid-feedback d-block mt-2">
              Choose an item for every line, or remove the empty lines
            </div>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>
    <!-- Terms & Actions Section -->
    <CRow>
      <CCol lg="12">
        <CCard class="mb-4 border-0">
          <CCardHeader class="bg-transparent border-0 p-4">
            <h2 class="h6 font-weight-bold mb-0">Additional information</h2>
          </CCardHeader>
          <CCardBody class="p-4">
            <fieldset :disabled="isLocked" class="border-0 p-0 m-0 min-w-0">
            <CRow>
              <CCol>
                <CTextarea
                  label="Terms and conditions (optional)"
                  v-model="obj.note"
                  placeholder="Payment terms, delivery instructions…"
                  rows="4"
                />
              </CCol>
            </CRow>
            </fieldset>
          </CCardBody>
          <CCardFooter class="bg-transparent border-0 p-4">
            <div class="d-flex justify-content-between">
              <CButton color="light" :disabled="saving" @click="cancel">
                Cancel
              </CButton>
              <CButton v-if="!isLocked" color="primary" :disabled="saving" @click="submit">
                <CIcon name="cil-check" class="mr-1" />
                {{ saving ? "Saving…" : obj.id ? "Save" : "Create purchase order" }}
              </CButton>
            </div>
          </CCardFooter>
        </CCard>
      </CCol>
    </CRow>
    <!-- Preview Section -->
    <CRow>
      <CCol lg="12">
        <CCard class="border-0">
          <CCardHeader class="bg-transparent border-0 p-4">
            <h2 class="h6 font-weight-bold mb-0">Preview</h2>
          </CCardHeader>
          <CCardBody class="p-4">
            <WidgetsReportPurchaseOrder
              :document="computedPreviewItem"
            />
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <!-- Status change confirmation -->
    <CModal
      :title="pendingStatus ? `Mark purchase order ${obj.orderNumber || ''} as ${badgeLabel('purchaseOrder', pendingStatus.name).toLowerCase()}?` : ''"
      :show.sync="statusConfirmOpen"
      :close-on-backdrop="!changingState"
    >
      <p class="mb-0" v-if="pendingStatus">
        The status changes from
        <strong>{{ badgeLabel("purchaseOrder", obj.statusDescription || "Draft") }}</strong>
        to <strong>{{ badgeLabel("purchaseOrder", pendingStatus.name) }}</strong>.
        <span v-if="pendingStatus.name === 'Cancelled' || pendingStatus.name === 'Rejected'">
          The order stays on record but is no longer counted as awaiting.
        </span>
      </p>
      <template #footer>
        <CButton color="light" :disabled="changingState" @click="statusConfirmOpen = false">
          {{ pendingStatus && (pendingStatus.name === 'Cancelled' || pendingStatus.name === 'Rejected') ? "Keep it" : "Cancel" }}
        </CButton>
        <CButton
          :color="pendingStatus && (pendingStatus.name === 'Cancelled' || pendingStatus.name === 'Rejected') ? 'danger' : 'primary'"
          :disabled="changingState"
          @click="changeState(pendingStatus)"
        >
          {{ changingState ? "Saving…" : pendingStatus ? `Mark as ${badgeLabel("purchaseOrder", pendingStatus.name).toLowerCase()}` : "" }}
        </CButton>
      </template>
    </CModal>

    <!-- Add Vendor Modal (shared) -->
    <VendorFormModal
      :show="addVendorFormPopup"
      :vendor="itemAddNewVendor"
      @close="addVendorFormPopup = false"
      @save="saveNewVendor"
    />

    <!-- Add Product Modal -->
    <CModal
      title="Add item"
      size="lg"
      :show.sync="addPoductFormPopup"
      @update:show="onProductPopupClosed"
    >
      <CRow form>
        <CCol md="12">
          <CInput
            label="Name *"
            v-model="itemAddNewProduct.name"
            :is-valid="productSubmitted && !itemAddNewProduct.name ? false : null"
            invalid-feedback="Enter a name for the item"
          />
          <CTextarea label="Description (optional)" rows="5" v-model="itemAddNewProduct.description" />
        </CCol>
      </CRow>
      <template #footer>
        <CButton color="light" :disabled="addingProduct" @click="addPoductFormPopup = false">Cancel</CButton>
        <CButton color="primary" :disabled="addingProduct" @click="saveNewProduct">
          {{ addingProduct ? "Adding…" : "Add item" }}
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import PurchaseOrderApi from "@/lib/purchaseOrderApi";
import VendorApi from "@/lib/vendorApi";
import PurchaseProductApi from "@/lib/purchaseProductApi";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import { mytToday, addDays, toApiDate, asDateString } from "@/utils/datetime";
import { formatMyr } from "@/utils/money";
import { badgeColor, badgeLabel } from "@/utils/statusBadges";
import WidgetsReportPurchaseOrder from "../widgets/WidgetsReportPurchaseOrder";
import InvoiceItemsComponent from "../components/InvoiceItemsComponent";
import VendorFormModal from "../components/VendorFormModal";

export default {
  name: "PurchaseOrder",
  components: {
    vSelect,
    WidgetsReportPurchaseOrder,
    InvoiceItemsComponent,
    VendorFormModal,
  },
  data: () => {
    return {
      itemPendingNewProduct: null,
      itemAddNewProduct: {},
      addPoductFormPopup: false,
      productSubmitted: false,
      addingProduct: false,

      itemAddNewVendor: {},
      addVendorFormPopup: false,

      creatingBill: false,
      copy: {
        createBill: "Create bill",
        creatingBill: "Creating…",
        needVendor: "Choose a vendor first",
        allBilled: "Everything on this order is billed",
        billsTitle: "Bills",
        lockedNote: "This order has bills, so it can't be changed. To edit it, delete its draft bills first.",
        billFailed: "Couldn't create the bill.",
      },

      purchaseorderStatuses: [],
      statusConfirmOpen: false,
      pendingStatus: null,
      changingState: false,

      issuedDate: mytToday(),
      expiryDate: mytToday(),
      purchaseOrderItems: [],

      selectedVendor: null,
      obj: {},
      submitted: false,
      saving: false,
      api: new PurchaseOrderApi(),
      vendorApi: new VendorApi(),
      productApi: new PurchaseProductApi(),
      vendorItems: [],
      productItems: [],
    };
  },
  mounted() {
    this.$store.commit("setPageNavItems", ["Expenses", "Purchase orders"]);
    this.fetchPurchaseOrderStatuses();
    this.refreshVendor();
    this.refreshProduct();
    this.resetObj();
    this.checkRouteQuery();
  },
  computed: {
    computedPreviewItem() {
      return {
        ...this.obj,
        vendor: this.hasVendor ? this.selectedVendor : this.obj.vendor,
        items: this.computedPurchaseOrderItems,
        issuedDate: this.computeIssuedDate,
        expiryDate: this.computeExpiryDate,
      };
    },

    computeProductItemsWithAddNew() {
      return [
        ...this.productItems,
        { id: "add_new", name: "+ Add new item" },
      ];
    },
    vendorItemsWithAddNew() {
      return [
        ...this.vendorItems,
        { id: "add_new", name: "+ Add new vendor" },
      ];
    },
    computeExpiryDate() {
      return asDateString(this.expiryDate);
    },
    computeIssuedDate() {
      return asDateString(this.issuedDate);
    },
    expectedDateValid() {
      return !this.computeIssuedDate || !this.computeExpiryDate || this.computeExpiryDate >= this.computeIssuedDate;
    },

    hasVendor() {
      return !!(this.selectedVendor && this.selectedVendor.id && this.selectedVendor.id !== "add_new");
    },

    itemsValid() {
      return this.purchaseOrderItems.every((item) => item.purchaseProduct && item.purchaseProduct.id);
    },

    // Only offer statuses other than the current one (a billed order can only be Accepted or Cancelled)
    statusTransitions() {
      const current = this.obj.statusDescription || "Draft";
      return (this.purchaseorderStatuses || []).filter(
        (s) => s.name !== current && (!this.hasBills || s.name === "Accepted" || s.name === "Cancelled")
      );
    },

    hasBills() {
      return !!(this.obj.bills && this.obj.bills.length);
    },
    // Once an order has bills it can no longer be edited
    isLocked() {
      return this.hasBills;
    },
    billingBadgeVisible() {
      return this.obj.billingStatus === "PartiallyBilled" || this.obj.billingStatus === "FullyBilled";
    },
    showCreateBill() {
      return !!this.obj.id && this.obj.statusDescription === "Accepted";
    },
    createBillBlockedReason() {
      if (!this.hasVendor) return this.copy.needVendor;
      if (this.obj.billingStatus === "FullyBilled") return this.copy.allBilled;
      return "";
    },
    canCreateBill() {
      return !this.createBillBlockedReason;
    },
    billLines() {
      return (this.obj.items || []).map((item, index) => {
        const ordered = Number(item.quantity) || 0;
        const billed = Number(item.billedQuantity) || 0;
        return {
          key: item.id || index,
          name: (item.purchaseProduct && item.purchaseProduct.name) || item.productName || "",
          ordered,
          billed,
          remaining: Math.max(0, ordered - billed),
        };
      });
    },

    computedPurchaseOrderItems() {
      return this.purchaseOrderItems.map((item) => {
        return {
          ...item,
          productName: item.purchaseProduct?.name || "",
          totalAmountPerItem: this.getTotalItemPrice(item),
        };
      });
    },

    // Normalized items for InvoiceItemsComponent (maps purchaseProduct to product)
    normalizedPurchaseOrderItems() {
      return this.purchaseOrderItems.map((item) => ({
        ...item,
        product: item.purchaseProduct, // Map purchaseProduct to product
        unit: item.unit || "pcs",
      }));
    },

    grandTotal() {
      var total = 0;
      for (var i = 0; i < this.purchaseOrderItems.length; i++) {
        var item = this.purchaseOrderItems[i];
        total += item.price * item.quantity;
      }
      return total;
    },
  },
  methods: {
    badgeColor,
    badgeLabel,
    formatCurrency(amount) {
      return `RM ${formatMyr(amount)}`;
    },

    // Normalized handler that converts product back to purchaseProduct
    handleProductSelectNormalized(selected, item) {
      if (selected?.id === "add_new") {
        this.itemPendingNewProduct = this.purchaseOrderItems.find((i) => i.id === item.id) || item;
        this.itemAddNewProduct = {};
        this.productSubmitted = false;
        this.addPoductFormPopup = true;
      } else {
        // Find the actual item in purchaseOrderItems and update purchaseProduct
        const actualItem = this.purchaseOrderItems.find((i) => i.id === item.id);
        if (actualItem) {
          actualItem.purchaseProduct = selected;
          if (selected && selected.defaultPrice) {
            actualItem.price = selected.defaultPrice;
          }
          if (selected && selected.defaultUnit) {
            actualItem.unit = selected.defaultUnit;
          }
        }
      }
    },

    addNew() {
      this.$router.push({ path: "/tenants/PurchaseOrder" });
    },
    checkRouteQuery() {
      // Handle data pre-filling from Purchase Quotation (vendor is selected once vendors load)
      if (this.$route.query.fromQuotation) {
        if (this.$route.query.quotationRef) {
          this.$set(this.obj, "reference", this.$route.query.quotationRef);
        }

        if (this.$route.query.description) {
          this.$set(this.obj, "title", this.$route.query.description);
        }
      }
    },
    cancel() {
      this.$router.push({ path: "/tenants/PurchaseOrderList" });
    },

    saveNewProduct() {
      this.productSubmitted = true;
      if (!this.itemAddNewProduct.name) return;
      this.addingProduct = true;
      this.productApi
        .create(this.itemAddNewProduct)
        .then((response) => {
          const addedProduct = response.result;
          this.refreshProduct();
          if (this.itemPendingNewProduct) this.itemPendingNewProduct.purchaseProduct = addedProduct;
          this.itemPendingNewProduct = null;
          this.$toast.success(addedProduct && addedProduct.name ? `${addedProduct.name} added` : "Item added");
          this.addPoductFormPopup = false;
        })
        .catch(({ data } = {}) => {
          this.$toast.error(`Couldn't add the item. ${helper.getErrorMessage(data, "Please try again.")}`);
        })
        .finally(() => {
          this.addingProduct = false;
        });
    },
    onProductPopupClosed(show) {
      if (!show) {
        // Closed without adding: clear the "+ Add new item" choice from the line
        if (this.itemPendingNewProduct && this.itemPendingNewProduct.purchaseProduct?.id === "add_new") {
          this.itemPendingNewProduct.purchaseProduct = null;
        }
        this.itemPendingNewProduct = null;
        this.itemAddNewProduct = {};
      }
    },

    saveNewVendor(vendor) {
      this.vendorApi
        .create(vendor)
        .then((response) => {
          const addedVendor = response.result;
          this.refreshVendor();
          this.selectedVendor = addedVendor;
          this.$toast.success(addedVendor && addedVendor.name ? `${addedVendor.name} added` : "Vendor added");
          this.addVendorFormPopup = false;
        })
        .catch(({ data } = {}) => {
          this.$toast.error(`Couldn't add the vendor. ${helper.getErrorMessage(data, "Please try again.")}`);
        });
      this.itemAddNewVendor = {};
    },

    handleVendorSelect(selected) {
      if (selected && selected.id === "add_new") {
        this.addNewVendor();
        this.selectedVendor = null; // Reset selection
      }
    },
    addNewVendor() {
      this.itemAddNewVendor = {};
      this.addVendorFormPopup = true;
    },

    createBill() {
      if (this.creatingBill || !this.canCreateBill) return;
      this.creatingBill = true;
      this.api
        .createBill(this.obj.id)
        .then((response) => {
          const result = response.result || {};
          this.$toast.success(`Bill ${result.billNo || ""} created as a draft`.replace("  ", " "));
          this.$router.push({ path: `/tenants/Bill/${result.billId}` });
        })
        .catch(({ data } = {}) => {
          this.$toast.error(`${this.copy.billFailed} ${helper.getErrorMessage(data, "Please try again.")}`);
          this.creatingBill = false;
        });
    },

    askChangeState(status) {
      this.pendingStatus = status;
      this.statusConfirmOpen = true;
    },
    changeState(item) {
      if (!item || !this.obj.id) return;
      this.changingState = true;
      this.obj.status = item.id;
      this.api
        .updateState(this.obj)
        .then(() => {
          this.$toast.success(`Purchase order marked as ${badgeLabel("purchaseOrder", item.name).toLowerCase()}`);
          this.statusConfirmOpen = false;
          this.resetObj();
        })
        .catch(({ data } = {}) => {
          this.$toast.error(`Couldn't change the status. ${helper.getErrorMessage(data, "Please try again.")}`);
        })
        .finally(() => {
          this.changingState = false;
        });
    },
    fetchPurchaseOrderStatuses() {
      this.api
        .getPurchaseOrderStatus()
        .then((response) => {
          this.purchaseorderStatuses = response.result || [];
        })
        .catch(() => {
          // Status actions are simply hidden when the list can't be loaded
          this.purchaseorderStatuses = [];
        });
    },

    setExpiryDate(e) {
      this.expiryDate = e;
    },
    setIssuedDate(e) {
      this.issuedDate = e;
    },
    updatePositions() {
      this.purchaseOrderItems.forEach((item, index) => {
        item.position = index + 1;
      });
    },

    moveItem(index, direction) {
      const newIndex = direction === "up" ? index - 1 : index + 1;
      if (newIndex >= 0 && newIndex < this.purchaseOrderItems.length) {
        const temp = this.purchaseOrderItems[index];
        this.$set(this.purchaseOrderItems, index, this.purchaseOrderItems[newIndex]);
        this.$set(this.purchaseOrderItems, newIndex, temp);
        this.updatePositions();
      }
    },

    onRemoveClaimItem(item) {
      const index = this.purchaseOrderItems.findIndex((i) => i.id === item.id);
      if (index > -1) {
        this.purchaseOrderItems.splice(index, 1);
        this.updatePositions();
      }
    },

    generateGUID() {
      return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
        /[xy]/g,
        function (c) {
          var r = (Math.random() * 16) | 0,
            v = c === "x" ? r : (r & 0x3) | 0x8;
          return v.toString(16);
        }
      );
    },
    addNewItem() {
      const newPosition = this.purchaseOrderItems.length + 1;
      this.purchaseOrderItems.push({
        id: this.generateGUID(),
        purchaseProduct: this.productItems.length > 0 ? this.productItems[0] : { name: "" },
        price: 0,
        quantity: 1,
        unit: "pcs",
        description: "",
        position: newPosition,
      });
    },
    getTotalItemPrice(item) {
      try {
        return item.quantity * item.price;
      } catch (error) {
        return 0;
      }
    },
    refreshProduct() {
      this.productApi
        .getListByCurrentBusiness()
        .then((response) => {
          this.productItems = response.result || [];
        })
        .catch(() => {});
    },

    refreshVendor() {
      this.vendorApi
        .getListByCurrentBusiness()
        .then((response) => {
          this.vendorItems = response.result || [];
          // Pre-select the vendor passed from a vendor quotation
          const vendorId = this.$route.query.vendorId;
          if (this.$route.query.fromQuotation && vendorId && !this.hasVendor) {
            const vendor = this.vendorItems.find((v) => v.id === vendorId);
            if (vendor) this.selectedVendor = vendor;
          }
        })
        .catch(() => {});
    },

    resetObj() {
      if (this.$route.params.id) {
        this.api
          .get(this.$route.params.id)
          .then((response) => {
            this.obj = response.result;
            this.issuedDate = asDateString(this.obj.issuedDate);
            this.expiryDate = asDateString(this.obj.dueDate);
            this.selectedVendor = this.obj.vendor;

            const items = this.obj.items || [];
            if (items.length > 0 && items[0].position == 0) {
              for (var i = 0; i < items.length; i++) {
                items[i].position = i + 1;
              }
            }

            this.purchaseOrderItems = items;
          })
          .catch(({ data } = {}) => {
            this.$toast.error(`Couldn't load this purchase order. ${helper.getErrorMessage(data, "Go back to the list and try again.")}`);
          });
      } else {
        this.obj = this.getEmptyObj();

        this.issuedDate = mytToday();
        this.expiryDate = addDays(this.issuedDate, 30);

        this.api
          .getNextNumber()
          .then((response) => {
            this.$set(this.obj, "orderNumber", response.result);
          })
          .catch(() => {});
      }
    },
    validateForm() {
      this.submitted = true;
      const firstInvalid = !this.hasVendor
        ? "vendorField"
        : !this.itemsValid
        ? "itemsField"
        : null;
      if (firstInvalid || !this.expectedDateValid) {
        this.$nextTick(() => this.focusField(firstInvalid));
        return false;
      }
      return true;
    },
    focusField(refName) {
      const ref = refName && this.$refs[refName];
      const el = ref && (ref.$el || ref);
      if (!el || !el.querySelector) return;
      el.scrollIntoView({ block: "center" });
      const input = el.querySelector("input, textarea, select");
      if (input) input.focus();
    },
    onSubmit() {
      if (this.saving || this.isLocked) return;
      const isEdit = !!this.obj.id;
      this.obj.items = this.computedPurchaseOrderItems;
      // Date-only fields go to the API as "yyyy-MM-dd" (never a Date: JSON turns it into UTC, 8 hours back)
      this.obj.issuedDate = toApiDate(this.computeIssuedDate) || null;
      this.obj.dueDate = toApiDate(this.computeExpiryDate) || null;

      this.obj.items.forEach((item) => {
        item.purchaseProductId = item.purchaseProduct.id;
      });

      this.obj.vendorId = this.selectedVendor.id;

      this.saving = true;
      const call = isEdit ? this.api.update(this.obj) : this.api.create(this.obj);
      call
        .then(() => {
          if (isEdit) {
            this.$toast.success("Purchase order saved");
            this.resetObj();
          } else {
            this.$toast.success("Purchase order created");
            this.$router.push({ path: "/tenants/PurchaseOrderList" });
          }
        })
        .catch(({ data } = {}) => {
          this.$toast.error(`Couldn't save the purchase order. ${helper.getErrorMessage(data, "Please try again.")}`);
        })
        .finally(() => {
          this.saving = false;
        });
    },
    getEmptyObj() {
      return {};
    },
    submit() {
      if (!this.validateForm()) return;
      this.onSubmit();
    },
  },
};
</script>

<style scoped>
.table {
  width: 100%;
  margin-top: 20px;
}
.min-w-0 {
  min-width: 0;
}
</style>
